/**
 * Email Templates - DA LesSablesWeb
 *
 * Templates HTML réutilisables pour tous les emails du CRM
 * Style épuré terre/teal — 100% inline styles pour compatibilité email
 * Palette : #5B4A3F (titres), #6B5D4F (corps), #4CAFA4 (boutons), #E8E0D4 (bordures)
 */

interface EmailTemplateOptions {
  title: string
  content: string
  actionButton?: {
    text: string
    url: string
  }
  footerText?: string
}

const STYLE = {
  heading: '#5B4A3F',
  subtitle: '#8B7D6B',
  body: '#6B5D4F',
  muted: '#A09485',
  button: '#4CAFA4',
  buttonText: '#ffffff',
  cardBg: '#ffffff',
  cardBorder: '#E8E0D4',
  infoBg: '#faf9f7',
  successBorder: '#4CAFA4',
  warningColor: '#d97706',
  alertBg: '#fef2f2',
  alertBorder: '#ef4444',
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
} as const

/**
 * Template de base pour tous les emails
 * 100% inline styles pour compatibilité email maximale
 */
export function createEmailTemplate(options: EmailTemplateOptions): string {
  const {
    title,
    content,
    actionButton,
    footerText = 'Cet email a &eacute;t&eacute; envoy&eacute; automatiquement. Merci de ne pas y r&eacute;pondre directement.',
  } = options

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f5f2ee;">
  <div style="font-family: ${STYLE.font}; max-width: 560px; margin: 0 auto; padding: 40px 20px;">
    <div style="text-align: center; margin-bottom: 32px;">
      <h1 style="color: ${STYLE.heading}; font-size: 24px; margin: 0;">Les Sables Web</h1>
      <p style="color: ${STYLE.subtitle}; font-size: 13px; margin: 4px 0 0;">Votre partenaire digital</p>
    </div>
    <div style="background: ${STYLE.cardBg}; border: 1px solid ${STYLE.cardBorder}; border-radius: 12px; padding: 32px;">
      <h2 style="color: ${STYLE.heading}; font-size: 20px; margin: 0 0 16px;">${title}</h2>
      ${content}
      ${actionButton ? `<div style="text-align: center; margin: 32px 0;">
        <a href="${actionButton.url}" style="background: ${STYLE.button}; color: ${STYLE.buttonText}; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-size: 15px; font-weight: 600; display: inline-block;">${actionButton.text}</a>
      </div>` : ''}
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid ${STYLE.cardBorder};">
        <p style="color: ${STYLE.body}; font-size: 14px; margin: 0;">Cordialement,</p>
        <p style="color: ${STYLE.button}; font-size: 14px; font-weight: 600; margin: 4px 0 0;">L'&eacute;quipe Les Sables Web</p>
      </div>
    </div>
    <div style="text-align: center; margin-top: 32px;">
      <p style="color: ${STYLE.muted}; font-size: 12px; margin: 0 0 8px;">${footerText}</p>
      <p style="color: ${STYLE.muted}; font-size: 12px; margin: 0;">&copy; ${new Date().getFullYear()} Les Sables Web &mdash; contact@lessablesweb.fr</p>
    </div>
  </div>
</body>
</html>`.trim()
}

/**
 * Inline info box (replaces CSS .info-box)
 */
function infoBox(content: string): string {
  return `<div style="background: ${STYLE.infoBg}; border-left: 4px solid ${STYLE.successBorder}; padding: 20px; margin: 20px 0; border-radius: 8px;">${content}</div>`
}

/**
 * Inline alert box (replaces CSS .alert-box)
 */
function alertBox(content: string): string {
  return `<div style="background: ${STYLE.alertBg}; border-left: 4px solid ${STYLE.alertBorder}; padding: 20px; margin: 20px 0; border-radius: 8px;">${content}</div>`
}

/** Inline paragraph style */
const pStyle = `color: ${STYLE.body}; font-size: 15px; line-height: 1.6; margin: 0 0 16px;`
/** Inline label style */
const labelStyle = `margin: 4px 0; color: ${STYLE.body}; font-size: 14px;`
/** Inline small/muted style */
const smallStyle = `color: ${STYLE.muted}; font-size: 13px; line-height: 1.5; margin: 24px 0 0;`

/**
 * Template pour confirmation de signature de devis
 */
export function createDevisSignatureEmail(params: {
  clientName: string
  devisNumero: string
  devisTitre: string
  montantTTC: number
}): string {
  const { clientName, devisNumero, devisTitre, montantTTC } = params

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Nous vous confirmons la signature de votre devis <strong>${devisNumero}</strong>.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>R&eacute;capitulatif :</strong></p>
      <p style="${labelStyle}"><strong>Titre :</strong> ${devisTitre}</p>
      <p style="${labelStyle}"><strong>Montant TTC :</strong> ${montantTTC.toFixed(2)}&euro;</p>
      <p style="${labelStyle}"><strong>Date de signature :</strong> ${new Date().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}</p>`)}
    <p style="${pStyle}">Nous allons maintenant proc&eacute;der &agrave; la r&eacute;alisation de votre projet.</p>
    <p style="${pStyle}">Vous recevrez prochainement plus d'informations sur les prochaines &eacute;tapes.</p>
  `

  return createEmailTemplate({
    title: 'Merci pour votre signature !',
    content,
  })
}

/**
 * Template pour envoi de devis
 */
export function createDevisEnvoiEmail(params: {
  clientName: string
  devisNumero: string
  devisTitre: string
  montantTTC: number
  dateValidite: string
  devisUrl: string
}): string {
  const { clientName, devisNumero, devisTitre, montantTTC, dateValidite, devisUrl } = params

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Nous avons le plaisir de vous transmettre votre devis <strong>${devisNumero}</strong>.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>D&eacute;tails du devis :</strong></p>
      <p style="${labelStyle}"><strong>Titre :</strong> ${devisTitre}</p>
      <p style="${labelStyle}"><strong>Montant TTC :</strong> ${montantTTC.toFixed(2)}&euro;</p>
      <p style="${labelStyle}"><strong>Validit&eacute; :</strong> jusqu'au ${new Date(dateValidite).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}</p>`)}
    <p style="${pStyle}">Pour consulter et signer votre devis en ligne, cliquez sur le bouton ci-dessous :</p>
    <p style="${smallStyle}"><em>Ce lien est personnel et s&eacute;curis&eacute;. Ne le partagez pas.</em></p>
  `

  return createEmailTemplate({
    title: 'Votre devis est pr&ecirc;t !',
    content,
    actionButton: {
      text: 'Consulter mon devis',
      url: devisUrl,
    },
  })
}

/**
 * Template pour envoi de questionnaire client
 */
export function createQuestionnaireEmail(params: {
  clientName: string
  projectTitle: string
  questionnaireUrl: string
}): string {
  const { clientName, projectTitle, questionnaireUrl } = params

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Afin de mieux comprendre vos besoins pour le projet <strong>${projectTitle}</strong>, nous avons pr&eacute;par&eacute; un questionnaire rapide.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>Pourquoi ce questionnaire ?</strong></p>
      <p style="${labelStyle}">Vos r&eacute;ponses nous permettront de personnaliser notre approche et de vous proposer la solution la plus adapt&eacute;e &agrave; vos attentes.</p>`)}
    <p style="${pStyle}">Le questionnaire ne prend que quelques minutes &agrave; remplir.</p>
    <p style="${pStyle}">Merci d'avance pour votre collaboration !</p>
  `

  return createEmailTemplate({
    title: 'Questionnaire projet',
    content,
    actionButton: {
      text: 'R&eacute;pondre au questionnaire',
      url: questionnaireUrl,
    },
  })
}

/**
 * Template pour relance
 */
export function createRelanceEmail(params: {
  contactName: string
  subject: string
  message: string
  actionUrl?: string
  actionText?: string
}): string {
  const { contactName, subject, message, actionUrl, actionText } = params

  const content = `
    <p style="${pStyle}">Bonjour ${contactName},</p>
    ${alertBox(`<p style="margin: 0; font-size: 15px; color: ${STYLE.body};"><strong>${subject}</strong></p>`)}
    <div style="white-space: pre-wrap; line-height: 1.8; color: ${STYLE.body}; font-size: 15px;">${message}</div>
    <p style="${pStyle}; margin-top: 24px;">N'h&eacute;sitez pas &agrave; nous contacter si vous avez besoin d'informations compl&eacute;mentaires.</p>
  `

  return createEmailTemplate({
    title: 'Rappel',
    content,
    actionButton:
      actionUrl && actionText
        ? {
            text: actionText,
            url: actionUrl,
          }
        : undefined,
  })
}

/**
 * Template pour email personnalisé (modal client)
 */
export function createCustomEmail(params: {
  clientName: string
  subject: string
  message: string
}): string {
  const { clientName, subject, message } = params

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    ${infoBox(`<div style="white-space: pre-wrap; line-height: 1.8; color: ${STYLE.body}; font-size: 15px;">${message}</div>`)}
  `

  return createEmailTemplate({
    title: subject,
    content,
  })
}

/**
 * Template pour confirmation de rendez-vous
 */
export function createRendezVousConfirmationEmail(params: {
  clientName: string
  dateHeure: string
  description?: string
}): string {
  const { clientName, dateHeure, description } = params

  const dateObj = new Date(dateHeure)
  const dateFormatted = dateObj.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const heureFormatted = dateObj.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Votre rendez-vous a &eacute;t&eacute; confirm&eacute; avec succ&egrave;s.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>D&eacute;tails du rendez-vous :</strong></p>
      <p style="${labelStyle}"><strong>Date :</strong> ${dateFormatted}</p>
      <p style="${labelStyle}"><strong>Heure :</strong> ${heureFormatted}</p>
      ${description ? `<p style="${labelStyle}"><strong>Objet :</strong> ${description}</p>` : ''}`)}
    <p style="${pStyle}">Nous vous attendons avec plaisir. Si vous avez un emp&ecirc;chement, merci de nous pr&eacute;venir au plus t&ocirc;t.</p>
  `

  return createEmailTemplate({
    title: 'Confirmation de rendez-vous',
    content,
  })
}

/**
 * Template pour la confirmation d'un RDV pris lors d'un appel de prospection.
 * Donne la date/heure du RDV et invite le prospect à s'inscrire sur l'espace client.
 */
export function createProspectAppointmentConfirmationEmail(params: {
  contactName: string
  dateHeure: string
  espaceClientUrl: string
}): string {
  const { contactName, dateHeure, espaceClientUrl } = params

  const dateObj = new Date(dateHeure)
  const dateFormatted = dateObj.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const heureFormatted = dateObj.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const content = `
    <p style="${pStyle}">Bonjour ${contactName},</p>
    <p style="${pStyle}">Nous vous confirmons la prise de rendez-vous suite &agrave; notre &eacute;change t&eacute;l&eacute;phonique.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>D&eacute;tails du rendez-vous :</strong></p>
      <p style="${labelStyle}"><strong>Date :</strong> ${dateFormatted}</p>
      <p style="${labelStyle}"><strong>Heure :</strong> ${heureFormatted}</p>`)}
    <p style="${pStyle}">Pour pr&eacute;parer au mieux notre &eacute;change et suivre votre projet, nous vous invitons &agrave; cr&eacute;er votre compte sur notre plateforme.</p>
  `

  return createEmailTemplate({
    title: 'Confirmation de votre rendez-vous',
    content,
    actionButton: {
      text: 'Acc&eacute;der &agrave; mon espace client',
      url: espaceClientUrl,
    },
  })
}

/**
 * Template pour envoi de facture
 */
export function createFactureEnvoiEmail(params: {
  clientName: string
  factureNumero: string
  montantTTC: number
  dateEcheance: string
  factureUrl?: string
}): string {
  const { clientName, factureNumero, montantTTC, dateEcheance, factureUrl } = params

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Nous vous transmettons votre facture <strong>${factureNumero}</strong>.</p>
    ${infoBox(`<p style="${labelStyle}"><strong>D&eacute;tails de la facture :</strong></p>
      <p style="${labelStyle}"><strong>Num&eacute;ro :</strong> ${factureNumero}</p>
      <p style="${labelStyle}"><strong>Montant TTC :</strong> ${montantTTC.toFixed(2)}&euro;</p>
      <p style="${labelStyle}"><strong>Date d'&eacute;ch&eacute;ance :</strong> ${new Date(dateEcheance).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}</p>`)}
    <p style="${pStyle}">Merci de proc&eacute;der au r&egrave;glement avant la date d'&eacute;ch&eacute;ance indiqu&eacute;e.</p>
    ${!factureUrl ? `<p style="${pStyle}">Vous trouverez la facture en pi&egrave;ce jointe de cet email.</p>` : ''}
    <p style="${smallStyle}">Pour toute question concernant cette facture, n'h&eacute;sitez pas &agrave; nous contacter.</p>
  `

  return createEmailTemplate({
    title: 'Votre facture',
    content,
    actionButton: factureUrl
      ? {
          text: 'Consulter ma facture',
          url: factureUrl,
        }
      : undefined,
  })
}

/**
 * Template pour remerciement après paiement de facture
 * Inclut un CTA vers la page d'avis Google
 */
export function createPaymentThankYouEmail(params: {
  clientName: string
  factureNumero: string
  montantTTC: number
  datePaiement?: string | null
  reviewUrl: string
}): string {
  const { clientName, factureNumero, montantTTC, datePaiement, reviewUrl } = params

  const dateStr = datePaiement
    ? new Date(datePaiement).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  const content = `
    <p style="${pStyle}">Bonjour ${clientName},</p>
    <p style="${pStyle}">Nous vous confirmons la bonne r&eacute;ception de votre r&egrave;glement. Un grand <strong>merci</strong> pour votre confiance&nbsp;!</p>
    ${infoBox(`<p style="${labelStyle}"><strong>R&eacute;capitulatif&nbsp;:</strong></p>
      <p style="${labelStyle}"><strong>Facture&nbsp;:</strong> ${factureNumero}</p>
      <p style="${labelStyle}"><strong>Montant r&eacute;gl&eacute;&nbsp;:</strong> ${montantTTC.toFixed(2)}&euro; TTC</p>
      ${dateStr ? `<p style="${labelStyle}"><strong>Date du paiement&nbsp;:</strong> ${dateStr}</p>` : ''}`)}
    <p style="${pStyle}">Votre satisfaction est notre meilleure r&eacute;compense. Si notre travail vous a plu, partager votre exp&eacute;rience en quelques mots nous aiderait &eacute;norm&eacute;ment.</p>
    <p style="${smallStyle}">Cela ne prend qu'une minute, et &ccedil;a fait toute la diff&eacute;rence pour notre petite &eacute;quipe.</p>
  `

  return createEmailTemplate({
    title: 'Merci pour votre règlement',
    content,
    actionButton: {
      text: 'Laisser un avis Google',
      url: reviewUrl,
    },
  })
}
